import { CircularProgress } from "@mui/material";
import DebouncedTextField from "@/components/DebouncedTextField";
import DeleteIcon from "@/components/app/DeleteIcon";
import Flex from "@/components/Flex";
import Text from "@/components/Text";
import { StyleAddButton } from "@/pages/app/provider/scheduling/appointment/encounter/default-forms/form-1/StyledAddButton";
import SectionAccordion from "./SectionAccordion";
import { useRegisterSectionSave } from "../pending-section-saves-context";
import HistoryEntryRow from "./HistoryEntryRow";
import { UseHistorySection } from "../hooks/use-history-section";
import { buildHistoryEntries } from "../readmission-f2f-summary-utils";
import {
  FREE_TEXT_MAX_ROWS,
  HISTORY_FREE_TEXT_LABEL,
  HISTORY_FREE_TEXT_PLACEHOLDER,
} from "../readmission-f2f-constant";

export interface HistorySectionLabels {
  namePlaceholder: string;
  addButtonLabel: string;
}

export interface HistorySectionProps {
  title: string;
  expanded: boolean;
  onToggle: () => void;
  section: UseHistorySection;
  nameLabel: string;
  labels: HistorySectionLabels;
  freeNoteValue?: string;
  onFreeNoteChange?: (value: string) => void;
}

const HistorySection = ({
  title,
  expanded,
  onToggle,
  section,
  nameLabel,
  labels,
  freeNoteValue,
  onFreeNoteChange,
}: HistorySectionProps) => {
  const {
    rows,
    draft,
    draftError,
    isLoading,
    updateRow,
    updateDraft,
    addDraft,
    saveRow,
    removeRow,
  } = section;
  // A typed but not yet added row is committed when the note is saved or signed.
  useRegisterSectionSave(title, !!draft.name.trim(), addDraft);

  return (
    <SectionAccordion
      title={title}
      expanded={expanded}
      onToggle={onToggle}
      summaryLines={buildHistoryEntries(rows, nameLabel, freeNoteValue)}
    >
      {isLoading ? (
        <Flex justifyContent="center" p={2}>
          <CircularProgress size={22} />
        </Flex>
      ) : (
        <>
          {rows.map((row) => (
            <HistoryEntryRow
              key={row.id}
              row={row}
              nameLabel={nameLabel}
              namePlaceholder={labels.namePlaceholder}
              onChange={(patch) => updateRow(row.id, patch)}
              onCommit={saveRow}
              action={
                <DeleteIcon
                  aria-label={`Remove ${nameLabel}`}
                  onClick={() => removeRow(row)}
                />
              }
            />
          ))}
          <HistoryEntryRow
            key={draft.id}
            row={draft}
            isDraft
            nameLabel={nameLabel}
            namePlaceholder={labels.namePlaceholder}
            error={draftError}
            onChange={updateDraft}
            action={
              <StyleAddButton
                w="10px"
                onClick={addDraft}
                label={labels.addButtonLabel}
              />
            }
          />
        </>
      )}

      <Flex flexDirection="column" gap={1}>
        <Text fontWeight="bold">{HISTORY_FREE_TEXT_LABEL}</Text>
        <DebouncedTextField
          size="small"
          fullWidth
          multiline
          minRows={3}
          maxRows={FREE_TEXT_MAX_ROWS}
          placeholder={HISTORY_FREE_TEXT_PLACEHOLDER}
          value={freeNoteValue ?? ""}
          onChange={(value) => onFreeNoteChange?.(value)}
        />
      </Flex>
    </SectionAccordion>
  );
};

export default HistorySection;
